import React, { createContext, useState, useContext, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { appParams } from '@/lib/app-params';
import { clearSessionToken } from '@/lib/customerSession';
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null); // Contains only { id, public_settings }

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(true);
      setAuthError(null);
      
      // First, check app public settings (with token if available)
      try {
        const headers = { 'X-App-Id': appParams.appId, 'Content-Type': 'application/json' };
        if (appParams.token) headers['Authorization'] = `Bearer ${appParams.token}`;
        const res = await fetch(`/api/apps/public/prod/public-settings/by-id/${appParams.appId}`, { headers });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          const err = new Error(errData?.message || 'Failed');
          err.status = res.status;
          err.data = errData;
          throw err;
        }
        const publicSettings = await res.json();
        setAppPublicSettings(publicSettings);
        
        // If we got the app public settings successfully, check if user is authenticated
        if (appParams.token) {
          await checkUserAuth();
        } else {
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
        }
        setIsLoadingPublicSettings(false);
      } catch (appError) {
        console.error('App settings error:', appError);
        
        // Handle app-level errors
        if (appError.status === 403 && (appError.data?.extra_data?.reason || appError.data?.reason)) {
          const reason = appError.data?.extra_data?.reason || appError.data?.reason;
          if (reason === 'auth_required') {
            setAuthError({
              type: 'auth_required',
              message: 'Authentication required'
            });
          } else if (reason === 'user_not_registered') {
            setAuthError({
              type: 'user_not_registered',
              message: 'User not registered for this app'
            });
          } else {
            setAuthError({
              type: reason,
              message: appError.message
            });
          }
        } else {
          // For other errors, still allow app to load but mark as not authenticated
          setAppPublicSettings({});
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
        }
        setIsLoadingPublicSettings(false);
      }
    } catch (error) {
      console.error('Unexpected error in checkAppState:', error);
      setAuthError({
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async () => {
    try {
      setIsLoadingAuth(true);

      const currentUser = await base44.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      setAuthError(null);

      // Save customer_data for cart session management — this drives getCartSessionId()
      // and ensures logged-in users get a stable basket_<square_customer_id> session
      if (currentUser.square_customer_id) {
        const oldGuestSession = localStorage.getItem('cart_session_id');
        const newSessionId = `basket_${currentUser.square_customer_id}`;

        // Set customer_data first so getCartSessionId() returns the new session
        localStorage.setItem('customer_data', JSON.stringify({
          square_customer_id: currentUser.square_customer_id,
          loyalty_account_id: currentUser.loyalty_account_id || null
        }));

        // Eagerly migrate guest cart items to the logged-in session
        const guestSessionToMigrate = oldGuestSession || localStorage.getItem('pending_cart_migration');
        if (guestSessionToMigrate && !guestSessionToMigrate.startsWith('basket_') && guestSessionToMigrate !== newSessionId) {
          try {
            const oldItems = await base44.entities.CartItem.filter({ session_id: guestSessionToMigrate });
            for (const item of oldItems) {
              if (item.converted_to_order) {
                await base44.entities.CartItem.delete(item.id);
              } else {
                await base44.entities.CartItem.update(item.id, {
                  session_id: newSessionId,
                  square_customer_id: currentUser.square_customer_id,
                  customer_name: [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || currentUser.full_name || 'Customer',
                  customer_phone: currentUser.phone_number || null,
                  customer_email: currentUser.email || null
                });
              }
            }
          } catch (_) { /* non-critical — cart migration failure shouldn't block login */ }
          localStorage.removeItem('pending_cart_migration');
          localStorage.removeItem('cart_session_id');
        }
      }

      setIsLoadingAuth(false);
    } catch (error) {
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      // Clear invalid token to prevent login loop
      if (error.status === 401 || error.status === 403) {
        localStorage.removeItem('base44_access_token');
        localStorage.removeItem('token');
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
      }
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await base44.auth.me();
      setUser(currentUser);

      // Keep customer_data in sync (especially if profile was just updated)
      if (currentUser.square_customer_id) {
        const oldGuest = localStorage.getItem('cart_session_id');
        if (oldGuest && !oldGuest.startsWith('basket_')) {
          localStorage.setItem('pending_cart_migration', oldGuest);
        }
        localStorage.setItem('customer_data', JSON.stringify({
          square_customer_id: currentUser.square_customer_id,
          loyalty_account_id: currentUser.loyalty_account_id || null
        }));
      }

      return currentUser;
    } catch (error) {
      return null;
    }
  };

  const logout = (shouldRedirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear cart, customer data, guest mode, and session token on logout
    localStorage.removeItem('customer_data');
    localStorage.removeItem('cart_session_id');
    localStorage.removeItem('guest_mode');
    clearSessionToken();
    
    if (shouldRedirect) {
      // Use the SDK's logout method which handles token cleanup and redirect
      base44.auth.logout(window.location.href);
    } else {
      // Just remove the token without redirect
      base44.auth.logout();
    }
  };

  const navigateToLogin = (next) => {
    window.location.href = next ? `/login?next=${encodeURIComponent(next)}` : '/login';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      navigateToLogin,
      checkAppState,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};